/** Applying the brand kit to elements: brand colours and text styles (pure scene commands). */
import type { Asset, Element, Fill, Scene, TextElement } from '@prism-graphics/scene-schema';
import type { BrandTextStyle } from '../api.ts';
import { ensureAsset, pruneAssets } from './commands.ts';
import { updateElement } from './tree.ts';

/** A colour on elements: text colour (a gradient fill is replaced), shape and box fill, line colour, mask none. */
export function applyColor(scene: Scene, ids: readonly string[], color: string): Scene {
  let s = scene;
  for (const id of ids) {
    s = updateElement(s, id, (el): Element => {
      switch (el.type) {
        case 'text': {
          const { fill: _f, ...style } = el.style;
          return { ...el, style: { ...style, color } };
        }
        case 'rect':
        case 'ellipse':
        case 'group': {
          const fill: Fill = { type: 'solid', color };
          return { ...el, style: { ...el.style, fill } } as Element;
        }
        case 'line':
          return { ...el, stroke: { ...el.stroke, color } };
        default:
          return el;
      }
    });
  }
  return s;
}

/** A text style on text elements: font (added to the scene's assets), weight, size, spacing, case and colour. */
export function applyTextStyle(scene: Scene, ids: readonly string[], style: BrandTextStyle, font: Asset | undefined): Scene {
  let s = font ? ensureAsset(scene, font) : scene;
  for (const id of ids) {
    s = updateElement(s, id, (el): Element => {
      if (el.type !== 'text') return el;
      const next: TextElement['style'] = {
        ...el.style,
        ...(font ? { font: font.hash } : {}),
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        fontSize: style.fontSize,
        lineHeight: style.lineHeight,
        letterSpacing: style.letterSpacing,
        textTransform: style.textTransform,
        ...(style.color ? { color: style.color } : {}),
      };
      const overflow = el.overflow.mode === 'shrink' && el.overflow.minFontSize > style.fontSize ? { mode: 'shrink' as const, minFontSize: style.fontSize } : el.overflow;
      return { ...el, style: next, overflow };
    });
  }
  return pruneAssets(s);
}

/** A text element's typography as a brand text style. */
export function textStyleOf(el: TextElement, name: string): BrandTextStyle {
  const st = el.style;
  return {
    name,
    font: st.font,
    fontWeight: st.fontWeight,
    fontStyle: st.fontStyle,
    fontSize: st.fontSize,
    lineHeight: st.lineHeight,
    letterSpacing: st.letterSpacing,
    textTransform: st.textTransform,
    color: st.color,
  };
}
